/**
 * Elegir una clave nueva con el enlace que llego por correo (ADM-01).
 *
 * El token viene en el fragmento de la URL (`#...`), que el navegador no manda
 * al servidor web: asi no queda en ningun registro. Se lee una vez al cargar y
 * se borra de la barra de direcciones, para que no quede en el historial.
 */
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Icono } from '../../componentes/comunes/Icono';
import { Seo } from '../../seo/Seo';
import { restablecerClave } from '../../servicios/autenticacion.servicio';
import { ErrorDeApi } from '../../servicios/cliente-api';
import { rutas } from '../../utilidades/rutas';

function leerToken(): string {
  return window.location.hash.replace(/^#/, '');
}

export function RestablecerClave() {
  const navegar = useNavigate();

  const [token] = useState(leerToken);
  const [claveNueva, setClaveNueva] = useState('');
  const [repetida, setRepetida] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  // Fuera del inicializador del estado: en modo estricto React lo llama dos
  // veces, y la segunda ya no encontraria el token.
  useEffect(() => {
    if (window.location.hash !== '') {
      window.history.replaceState(window.history.state, '', window.location.pathname);
    }
  }, []);

  async function enviar(evento: FormEvent<HTMLFormElement>): Promise<void> {
    evento.preventDefault();
    setError(null);

    if (claveNueva !== repetida) {
      setError('Las dos claves no son iguales.');
      return;
    }

    setEnviando(true);

    try {
      await restablecerClave({ token, claveNueva });
      navegar(rutas.administracion.acceso(), {
        replace: true,
        state: { aviso: 'Listo, tu clave quedó cambiada. Ya puedes entrar con la nueva.' },
      });
    } catch (fallo) {
      setError(
        fallo instanceof ErrorDeApi
          ? (fallo.detalles?.[0]?.mensaje ?? fallo.message)
          : 'No pudimos conectarnos con el servidor. Intenta de nuevo.',
      );
      setEnviando(false);
    }
  }

  return (
    <>
      <Seo
        titulo="Elegir una clave nueva"
        descripcion="Restablecer la clave del panel de administración."
        ruta={rutas.administracion.restablecerClave()}
        sinIndexar
      />

      <main className="acceso">
        <div className="acceso-caja">
          <div className="acceso-marca">
            <span className="acceso-icono">
              <Icono nombre="llave" />
            </span>
          </div>

          <h1>Elige una clave nueva</h1>

          {token === '' ? (
            <>
              <p className="acceso-error" role="alert">
                <Icono nombre="alerta" />
                <span>
                  Este enlace está incompleto. Abre el que llegó por correo, o pide uno nuevo.
                </span>
              </p>
              <p className="acceso-enlace">
                <Link to={rutas.administracion.recuperarClave()}>Pedir un enlace nuevo</Link>
              </p>
            </>
          ) : (
            <>
              <p className="acceso-bajada">
                Tu clave actual sigue funcionando hasta que guardes la nueva.
              </p>

              <form
                onSubmit={(evento) => {
                  void enviar(evento);
                }}
                noValidate
              >
                {error !== null ? (
                  <p className="acceso-error" role="alert">
                    <Icono nombre="alerta" />
                    <span>{error}</span>
                  </p>
                ) : null}

                <div className="campo">
                  <label htmlFor="restablecer-nueva">Clave nueva</label>
                  <input
                    id="restablecer-nueva"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={10}
                    aria-describedby="ayuda-restablecer"
                    value={claveNueva}
                    onChange={(evento) => {
                      setClaveNueva(evento.target.value);
                    }}
                  />
                  <p className="campo-ayuda" id="ayuda-restablecer">
                    Al menos 10 caracteres. Una frase que recuerdes sirve mejor que ocho
                    caracteres raros.
                  </p>
                </div>

                <div className="campo">
                  <label htmlFor="restablecer-repetida">Repite la clave nueva</label>
                  <input
                    id="restablecer-repetida"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={repetida}
                    onChange={(evento) => {
                      setRepetida(evento.target.value);
                    }}
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primario btn-lg btn-bloque"
                  disabled={enviando}
                >
                  {enviando ? 'Guardando…' : 'Guardar la clave'}
                </button>
              </form>

              <p className="acceso-enlace">
                <Link to={rutas.administracion.acceso()}>Volver a entrar</Link>
              </p>
            </>
          )}
        </div>
      </main>
    </>
  );
}
